import { Box, Grid } from '@mui/material';
import PropTypes from 'prop-types';
import { useEffect, useMemo } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useDispatch, useSelector } from 'react-redux';
import { saveTransitionVendor } from '../../../../api/transitions';
import { getTransitionVendorsList } from '../../../../redux/slices/transitions';
import { enqueueSnackbar } from '../../../../reusable-components/snackbar';
import { groupedFieldsStyle } from '../TransitionStyles';
import CheckboxInput from './FormInputs/CheckboxInput';
import FileDrop from './FormInputs/FileDrop';
import FreeSoloAutocompleteInput from './FormInputs/FreeSoloAutocompleteInput';
import FormTitle from './FormTitle';
import { createEmptyVendor } from './vendorFieldDefaults';

FormVendorField.propTypes = {
  fieldName: PropTypes.string,
  fieldAttributes: PropTypes.object,
  contractFileTypeId: PropTypes.number,
  invoiceFileTypeId: PropTypes.number,
};

export default function FormVendorField({ fieldName, fieldAttributes, contractFileTypeId, invoiceFileTypeId }) {
  const { control, setValue, watch } = useFormContext();
  const dispatch = useDispatch();
  const {
    data: { transitionVendors, platformVendors },
  } = useSelector((state) => state.transitions);

  const companyId = watch('companyId');
  const fieldValue = watch(fieldName);
  const vendorRootPath = `${fieldName}.vendors.0`;
  const vendorSelectionPath = `${vendorRootPath}.vendor`;
  const vendorIsFacHappyPath = `${vendorRootPath}.isFacHappy`;
  const contractFilesPath = `${fieldName}.vendors.0.contractFiles`;
  const invoiceFilesPath = `${fieldName}.vendors.0.invoiceFiles`;

  const vendorOptions = useMemo(
    () => [...platformVendors, ...(transitionVendors[companyId] ?? [])].sort((a, b) => a.label.localeCompare(b.label)),
    [platformVendors, transitionVendors, companyId]
  );

  const contractFilesMethods = useFieldArray({
    control,
    name: contractFilesPath,
    keyName: '_id',
  });

  const invoiceFilesMethods = useFieldArray({
    control,
    name: invoiceFilesPath,
    keyName: '_id',
  });

  useEffect(() => {
    if ((fieldValue?.vendors?.length ?? 0) > 0) return;

    setValue(`${fieldName}.vendors`, [createEmptyVendor({ allowMultipleVendors: false })], {
      shouldDirty: false,
      shouldTouch: false,
      shouldValidate: false,
    });
  }, [fieldName, fieldValue?.vendors?.length, setValue]);

  const onContractFileAdded = (files) => {
    files.forEach((res) => {
      contractFilesMethods.append(res[0]);
    });
  };

  const onContractFileRemoved = (file) => {
    const currentFiles = watch(contractFilesPath) ?? [];
    const fileToRemove = currentFiles.findIndex((f) => f.id === file.id);
    contractFilesMethods.update(fileToRemove, file);
  };

  const onInvoiceFileAdded = (files) => {
    files.forEach((res) => {
      invoiceFilesMethods.append(res[0]);
    });
  };

  const onInvoiceFileRemoved = (file) => {
    const currentFiles = watch(invoiceFilesPath) ?? [];
    const fileToRemove = currentFiles.findIndex((f) => f.id === file.id);
    invoiceFilesMethods.update(fileToRemove, file);
  };

  const saveNewVendor = async (vendor) => {
    await saveTransitionVendor(vendor, companyId)
      .then((resp) => {
        if (resp.data.success) {
          enqueueSnackbar(resp.data.message, { variant: 'success' });
          dispatch(getTransitionVendorsList(companyId));
          setValue(vendorSelectionPath, resp.data.data, { shouldDirty: true });
        } else {
          enqueueSnackbar(resp.data.message, { variant: 'error' });
        }
      })
      .catch(() => {
        enqueueSnackbar('Failed to save new vendor', { variant: 'error' });
      });
  };

  const additionalCheck = (option, value) => option?.vendorCategoryId === value?.vendorCategoryId;

  return (
    <Box sx={{ mt: 1, ...groupedFieldsStyle }}>
      <Grid container spacing={1}>
        <Grid xs={12} item>
          <Controller
            name={vendorSelectionPath}
            control={control}
            render={() => (
              <FreeSoloAutocompleteInput
                fieldName={vendorSelectionPath}
                options={vendorOptions}
                isEditable={fieldAttributes?.isEditable}
                saveNewValue={saveNewVendor}
                additionalCheck={additionalCheck}
              />
            )}
          />
        </Grid>

        <Grid xs={12} sm={6} item>
          <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <FormTitle fieldAttributes={{ questionTitle: 'Most recent invoice', fieldType: 'file' }} />
            <Controller
              name={invoiceFilesPath}
              control={control}
              render={() => (
                <FileDrop
                  fieldAttributes={{
                    ...fieldAttributes,
                    fileTypeId: invoiceFileTypeId,
                  }}
                  onFileRemoved={onInvoiceFileRemoved}
                  onFileAdded={onInvoiceFileAdded}
                  files={invoiceFilesMethods.fields?.filter((f) => !f.deleteFlag) ?? []}
                />
              )}
            />
          </Box>
        </Grid>

        <Grid xs={12} sm={6} item>
          <Box display="flex" flexDirection="column" justifyContent="space-between" height="100%">
            <FormTitle fieldAttributes={{ questionTitle: 'Copy of contract', fieldType: 'file' }} />
            <Controller
              name={contractFilesPath}
              control={control}
              render={() => (
                <FileDrop
                  fieldAttributes={{
                    ...fieldAttributes,
                    fileTypeId: contractFileTypeId,
                  }}
                  onFileRemoved={onContractFileRemoved}
                  onFileAdded={onContractFileAdded}
                  files={contractFilesMethods.fields?.filter((f) => !f.deleteFlag) ?? []}
                />
              )}
            />
          </Box>
        </Grid>

        <Grid xs={12} item>
          <Controller
            name={vendorIsFacHappyPath}
            control={control}
            render={() => (
              <CheckboxInput
                fieldName={vendorIsFacHappyPath}
                fieldAttributes={{ label: 'Is facility happy with supplier?', ...fieldAttributes }}
              />
            )}
          />
        </Grid>
      </Grid>
    </Box>
  );
}
